import {Editor} from 'slate';
import {ReactEditor} from 'slate-react';

import {
  ReportPromptAnchor,
  ReportPromptAnchorRect
} from '../../../store/reportStore/reportDialogStore';

const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 120;
const VIEWPORT_GAP = 8;
const ANCHOR_GAP = 10;

export interface PopoverPosition {
  top: number;
  left: number;
  placement: 'above' | 'below';
}

export function getEditorSelectionAnchorRect(
  editor: Editor
): ReportPromptAnchorRect | null {
  const {selection} = editor;
  if (!selection) return null;

  try {
    const domRange = ReactEditor.toDOMRange(editor as ReactEditor, selection);
    const rect = domRange.getBoundingClientRect();
    const clientRect = domRange.getClientRects()[0];
    const anchorRect =
      rect && (rect.width !== 0 || rect.height !== 0) ? rect : clientRect;

    if (!anchorRect) return null;

    return {
      top: anchorRect.top,
      left: anchorRect.left,
      width: anchorRect.width,
      height: anchorRect.height
    };
  } catch {
    return null;
  }
}

export function resolveAnchorRect(
  anchor?: ReportPromptAnchor
): ReportPromptAnchorRect | null {
  if (!anchor) return null;

  if (anchor.type === 'rect') {
    return anchor.rect;
  }

  const rect = anchor.element.getBoundingClientRect();

  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height
  };
}

export function computePopoverPosition(
  anchorRect: ReportPromptAnchorRect | null,
  popoverWidth = POPOVER_WIDTH,
  popoverHeight = POPOVER_HEIGHT
): PopoverPosition {
  const fallbackLeft = Math.max(
    VIEWPORT_GAP,
    (window.innerWidth - popoverWidth) / 2
  );
  const fallbackTop = Math.max(
    VIEWPORT_GAP,
    (window.innerHeight - popoverHeight) / 2
  );

  if (!anchorRect) {
    return {top: fallbackTop, left: fallbackLeft, placement: 'below'};
  }

  const centeredLeft =
    anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  const left = Math.max(
    VIEWPORT_GAP,
    Math.min(centeredLeft, window.innerWidth - popoverWidth - VIEWPORT_GAP)
  );

  const roomAbove = anchorRect.top - VIEWPORT_GAP;
  const roomBelow =
    window.innerHeight - anchorRect.top - anchorRect.height - VIEWPORT_GAP;

  let top = anchorRect.top + anchorRect.height + ANCHOR_GAP;
  let placement: PopoverPosition['placement'] = 'below';

  if (roomBelow < popoverHeight && roomAbove >= popoverHeight) {
    top = anchorRect.top - popoverHeight - ANCHOR_GAP;
    placement = 'above';
  } else if (roomBelow < popoverHeight && roomAbove < popoverHeight) {
    top = Math.max(
      VIEWPORT_GAP,
      Math.min(
        anchorRect.top + anchorRect.height + ANCHOR_GAP,
        window.innerHeight - popoverHeight - VIEWPORT_GAP
      )
    );
  }

  return {top, left, placement};
}
